import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import API from "../lib/api";
import { CardListSkeleton } from "../components/Skeletons";
import ConsolidatedInvoiceDialog from "../components/ConsolidatedInvoiceDialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { fmtDate } from "../lib/dateUtil";
import {
  Plus, MagnifyingGlass, Receipt, Trash, Copy, PencilSimple,
  ArrowRight, Funnel, FilePdf, DownloadSimple, CheckCircle, X
} from "@phosphor-icons/react";

const STATUS_STYLES = {
  draft: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
  sent: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  approved: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  declined: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  expired: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  converted: "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300",
};

export default function InvoicesPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [invoices, setQuotes] = useState([]);
  const [total, setTotal] = useState(0);
  const [mainClients, setMainClients] = useState([]);
  const [consolidatedFor, setConsolidatedFor] = useState(null);
  useEffect(() => { API.get("/clients", { params: { kind: "main", limit: 500 } }).then(({ data }) => setMainClients(data.clients || [])).catch(() => {}); }, []);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [pendingFilter, setPendingFilter] = useState(searchParams.get("pending_verification") === "1");
  const [stats, setStats] = useState({});
  const [selectedQuotes, setSelectedQuotes] = useState([]);
  const [batchExporting, setBatchExporting] = useState(false);
  const [actingId, setActingId] = useState(null);
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const fetchQuotes = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (pendingFilter) params.pending_verification = "true";
      else if (statusFilter) params.status = statusFilter;
      const [quotesRes, statsRes] = await Promise.all([
        API.get("/invoices", { params }),
        API.get("/invoices/stats"),
      ]);
      setQuotes(quotesRes.data.invoices || []);
      setTotal(quotesRes.data.total || 0);
      setStats(statsRes.data || {});
    } catch { toast.error("Failed to load invoices"); }
    finally { setLoading(false); }
  }, [search, statusFilter, pendingFilter]);

  useEffect(() => { fetchQuotes(); }, [fetchQuotes]);

  // Sync pendingFilter to URL so Dashboard banner deep-link works
  useEffect(() => {
    if (pendingFilter && searchParams.get("pending_verification") !== "1") {
      setSearchParams({ pending_verification: "1" });
    } else if (!pendingFilter && searchParams.get("pending_verification") === "1") {
      setSearchParams({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingFilter]);

  const handleDuplicate = async (id) => {
    try {
      const { data } = await API.post(`/invoices/${id}/duplicate`);
      toast.success(`Invoice duplicated: ${data.reference}`);
      fetchQuotes();
    } catch { toast.error("Failed to duplicate"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this invoice?")) return;
    try {
      await API.delete(`/invoices/${id}`);
      toast.success("Invoice deleted");
      fetchQuotes();
    } catch { toast.error("Failed to delete"); }
  };

  const handleDownloadPdf = async (id, view, ref) => {
    try {
      const response = await API.get(`/invoices/${id}/pdf?view=${view}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `${ref || "invoice"}_${view}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Failed to download PDF"); }
  };

  const handleBatchExport = async (view) => {
    if (!selectedQuotes.length) return;
    setBatchExporting(true);
    try {
      const response = await API.post("/invoices/batch-pdf", { quote_ids: selectedQuotes, view }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/zip" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `quotes_${view}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${selectedQuotes.length} PDFs exported`);
      setSelectedQuotes([]);
    } catch { toast.error("Failed to export"); }
    finally { setBatchExporting(false); }
  };

  const toggleSelectQuote = (id) => setSelectedQuotes(prev => prev.includes(id) ? prev.filter(q => q !== id) : [...prev, id]);
  const toggleSelectAll = () => setSelectedQuotes(prev => prev.length === invoices.length ? [] : invoices.map(q => q.id));

  const byStatus = stats.by_status || {};
  const pendingCount = stats.pending_verification || 0;
  const statFilters = [
    { key: "", label: "All", count: total },
    { key: "draft", label: "Draft", count: byStatus.draft || 0 },
    { key: "sent", label: "Sent", count: byStatus.sent || 0 },
    { key: "paid", label: "Paid", count: byStatus.paid || 0 },
    { key: "overdue", label: "Overdue", count: byStatus.overdue || 0 },
    { key: "cancelled", label: "Cancelled", count: byStatus.cancelled || 0 },
  ];

  const handleVerifyPaid = async (id, ref) => {
    setActingId(id);
    try {
      await API.post(`/invoices/${id}/verify-paid`);
      toast.success(`Verified — ${ref} marked paid`);
      fetchQuotes();
    } catch (e) { toast.error(e.response?.data?.detail || "Verify failed"); }
    finally { setActingId(null); }
  };

  const handleRejectSubmit = async () => {
    if (!rejectFor) return;
    setActingId(rejectFor.id);
    try {
      await API.post(`/invoices/${rejectFor.id}/reject-paid-claim`, { reason: rejectReason });
      toast.success("Claim rejected");
      setRejectFor(null); setRejectReason("");
      fetchQuotes();
    } catch (e) { toast.error(e.response?.data?.detail || "Reject failed"); }
    finally { setActingId(null); }
  };

  return (
    <div data-testid="invoices-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center">
            <Receipt size={18} className="text-white" weight="fill" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold font-[Chivo] tracking-tight">Invoices</h1>
            <p className="text-xs text-muted-foreground">{total} invoice{total !== 1 ? "s" : ""}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {mainClients.length > 0 && (
            <select value={consolidatedFor?.id || ""} onChange={(e) => setConsolidatedFor(mainClients.find(c => c.id === e.target.value) || null)} className="h-9 rounded-md border border-input bg-background px-2 text-xs" data-testid="consolidated-client-select">
              <option value="">Consolidated invoice for…</option>
              {mainClients.map(c => <option key={c.id} value={c.id}>{c.company_name || `${c.first_name} ${c.last_name}`} ({c.sub_client_count} sub-client{c.sub_client_count === 1 ? "" : "s"})</option>)}
            </select>
          )}
          <Button onClick={() => navigate("/invoices/new")} data-testid="create-invoice-btn">
            <Plus size={16} className="mr-1.5" /> New Invoice
          </Button>
        </div>
      </div>
      {consolidatedFor && <ConsolidatedInvoiceDialog client={consolidatedFor} open={!!consolidatedFor} onClose={() => setConsolidatedFor(null)} />}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search invoices..."
            className="pl-9 h-9 text-sm"
            data-testid="invoice-search"
          />
        </div>
        <div className="flex items-center gap-1 overflow-x-auto" data-testid="invoice-status-filters">
          {statFilters.map(f => (
            <button key={f.key} onClick={() => { setPendingFilter(false); setStatusFilter(f.key); }}
              className={`px-2.5 py-1 rounded-md text-xs font-medium whitespace-nowrap transition-colors ${!pendingFilter && statusFilter === f.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}
              data-testid={`filter-${f.key || "all"}`}
            >
              {f.label} ({f.count})
            </button>
          ))}
          {pendingCount > 0 && (
            <button
              onClick={() => { setPendingFilter(true); setStatusFilter(""); }}
              className={`px-2.5 py-1 rounded-md text-xs font-semibold whitespace-nowrap transition-colors ml-1 ${pendingFilter ? "bg-amber-500 text-white" : "bg-amber-100 text-amber-800 hover:bg-amber-200 dark:bg-amber-900/40 dark:text-amber-200"}`}
              data-testid="filter-pending-verification"
            >
              Pending verification ({pendingCount})
            </button>
          )}
        </div>
      </div>

      {/* Batch Actions */}
      {selectedQuotes.length > 0 && (
        <div className="flex items-center gap-3 mb-4 p-3 bg-primary/5 border border-primary/20 rounded-md" data-testid="batch-actions-bar">
          <span className="text-sm font-medium">{selectedQuotes.length} selected</span>
          <Button size="sm" onClick={() => handleBatchExport("client")} disabled={batchExporting} data-testid="batch-client-pdf">
            <DownloadSimple size={14} className="mr-1" /> Client PDFs
          </Button>
          <Button size="sm" variant="outline" onClick={() => handleBatchExport("admin")} disabled={batchExporting} data-testid="batch-admin-pdf">
            <DownloadSimple size={14} className="mr-1" /> Admin PDFs
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setSelectedQuotes([])} data-testid="batch-clear">Clear</Button>
        </div>
      )}

      {/* List */}
      {loading ? (
        <CardListSkeleton rows={6} showHeader={false} />
      ) : invoices.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-border rounded-lg" data-testid="no-invoices">
          <Receipt size={36} className="mx-auto text-muted-foreground/40 mb-2" />
          <p className="text-sm text-muted-foreground">No invoices found</p>
          <Button variant="outline" size="sm" className="mt-3" onClick={() => navigate("/invoices/new")}>Create your first invoice</Button>
        </div>
      ) : (
        <div className="space-y-2">
          {invoices.map(q => (
            <div key={q.id} className="border border-border rounded-lg p-4 bg-card hover:shadow-sm transition-shadow" data-testid={`invoice-row-${q.id}`}>
              <div className="flex items-start gap-3">
                <input type="checkbox" checked={selectedQuotes.includes(q.id)} onChange={() => toggleSelectQuote(q.id)} className="mt-1.5 accent-primary shrink-0" data-testid={`select-invoice-${q.id}`} />
                <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/invoices/${q.id}`)}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="text-[10px] font-mono text-muted-foreground">{q.reference}</span>
                        {q.client_po_number && <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 font-semibold" data-testid={`invoice-po-${q.id}`}>PO {q.client_po_number}</span>}
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_STYLES[q.status] || STATUS_STYLES.draft}`}>
                          {q.status?.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
                        </span>
                        {q.client_marked_paid_at && q.status !== "paid" && q.status !== "cancelled" && (
                          <Badge className="text-[9px] bg-amber-100 text-amber-800 hover:bg-amber-100 dark:bg-amber-900/40 dark:text-amber-200" data-testid={`claim-badge-${q.id}`}>
                            Client claimed paid
                          </Badge>
                        )}
                        {q.converted_to && (
                          <Badge variant="outline" className="text-[9px]">
                            {q.converted_to.type === "job" ? "Job" : "Project"}: {q.converted_to.reference}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm font-semibold truncate">{q.title}</p>
                      <div className="flex items-center gap-3 mt-1 text-[10px] text-muted-foreground">
                        {q.client_name && <span>{q.client_name}</span>}
                        {q.street_address && <span>{q.street_address}, {q.town_city}</span>}
                        <span>{(q.line_items || []).length} item{(q.line_items || []).length !== 1 ? "s" : ""}</span>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-base font-bold font-[Chivo]">&pound;{(q.vat_enabled ? (q.total_inc_vat || q.grand_total || 0) : (q.grand_total || 0)).toLocaleString("en-GB", { minimumFractionDigits: 2 })}</p>
                      {q.vat_enabled && <p className="text-[9px] text-muted-foreground">inc. VAT</p>}
                      <p className="text-[10px] text-muted-foreground">{fmtDate(q.created_at)}</p>
                    </div>
                  </div>
                </div>
              </div>
              {/* Claim detail strip — only when client has claimed paid and not yet verified */}
              {q.client_marked_paid_at && q.status !== "paid" && q.status !== "cancelled" && (
                <div className="mt-2 px-2 py-1.5 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/40 rounded text-[11px] text-amber-900 dark:text-amber-200 flex items-center gap-2 flex-wrap" data-testid={`claim-strip-${q.id}`}>
                  <span><strong>{q.client_marked_paid_by || "Client"}</strong> says paid</span>
                  {q.client_marked_paid_method && <span>via {q.client_marked_paid_method}</span>}
                  {q.client_marked_paid_reference && <span>&middot; ref <span className="font-mono">{q.client_marked_paid_reference}</span></span>}
                  {q.client_marked_paid_notes && <span className="italic text-amber-700 dark:text-amber-300/80">"{q.client_marked_paid_notes}"</span>}
                </div>
              )}
              {/* Actions row */}
              <div className="flex items-center gap-1 mt-2 pt-2 border-t border-border/50">
                <Button variant="ghost" size="sm" className="h-7 text-[10px] px-2" onClick={() => navigate(`/invoices/${q.id}`)} data-testid={`view-invoice-${q.id}`}>
                  <PencilSimple size={12} className="mr-1" /> View
                </Button>
                <Button variant="ghost" size="sm" className="h-7 text-[10px] px-2" onClick={() => handleDuplicate(q.id)} data-testid={`dup-invoice-${q.id}`}>
                  <Copy size={12} className="mr-1" /> Duplicate
                </Button>
                <Button variant="ghost" size="sm" className="h-7 text-[10px] px-2" onClick={() => handleDownloadPdf(q.id, "client", q.reference)} data-testid={`pdf-client-${q.id}`}>
                  <FilePdf size={12} className="mr-1" /> Client PDF
                </Button>
                <Button variant="ghost" size="sm" className="h-7 text-[10px] px-2" onClick={() => handleDownloadPdf(q.id, "admin", q.reference)} data-testid={`pdf-admin-${q.id}`}>
                  <FilePdf size={12} className="mr-1" /> Admin PDF
                </Button>
                {q.client_marked_paid_at && q.status !== "paid" && q.status !== "cancelled" && (
                  <>
                    <Button
                      size="sm"
                      className="h-7 text-[10px] px-2 bg-emerald-600 hover:bg-emerald-700 text-white"
                      onClick={() => handleVerifyPaid(q.id, q.reference)}
                      disabled={actingId === q.id}
                      data-testid={`verify-paid-${q.id}`}
                    >
                      <CheckCircle size={12} weight="fill" className="mr-1" /> Verify & Mark Paid
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-[10px] px-2 text-rose-600 border-rose-200 dark:border-rose-900/40"
                      onClick={() => { setRejectFor(q); setRejectReason(""); }}
                      disabled={actingId === q.id}
                      data-testid={`reject-paid-${q.id}`}
                    >
                      <X size={12} className="mr-1" /> Reject
                    </Button>
                  </>
                )}
                <div className="flex-1" />
                <Button variant="ghost" size="sm" className="h-7 text-[10px] px-2 text-destructive hover:text-destructive" onClick={() => handleDelete(q.id)} data-testid={`del-invoice-${q.id}`}>
                  <Trash size={12} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Reject claim dialog */}
      {rejectFor && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => actingId === null && setRejectFor(null)}>
          <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-md p-5 space-y-4" onClick={e => e.stopPropagation()} data-testid="invoice-reject-dialog">
            <div>
              <h3 className="font-semibold font-[Chivo] text-base">Reject paid claim</h3>
              <p className="text-[11px] text-muted-foreground font-mono mt-0.5">{rejectFor.reference} &middot; {rejectFor.client_name}</p>
            </div>
            <p className="text-xs text-muted-foreground">The invoice will return to outstanding. Reason is recorded in the timeline.</p>
            <div>
              <label className="text-xs font-medium">Reason (optional)</label>
              <textarea
                rows={3}
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
                placeholder="e.g. Funds not received in our account."
                className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background text-sm"
                data-testid="invoice-reject-reason"
              />
            </div>
            <div className="flex items-center justify-end gap-2">
              <Button variant="outline" onClick={() => setRejectFor(null)} disabled={actingId === rejectFor.id} data-testid="invoice-reject-cancel">Cancel</Button>
              <Button onClick={handleRejectSubmit} disabled={actingId === rejectFor.id} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="invoice-reject-submit">
                {actingId === rejectFor.id ? "Rejecting…" : "Reject claim"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
